import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { importProvidersFrom, APP_INITIALIZER } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { AuthHeaderInterceptor } from './app/interceptors/auth-header.interceptor';
import { PageLoadingInterceptor } from './app/interceptors/page-loading.interceptor';
import { RouteReuseStrategy } from '@angular/router';
import { ChatReuseStrategy } from './app/strategies/chat-reuse-strategy';
import { MsalModule, MsalService, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { msalConfig, msalGuardConfig, msalInterceptorConfig } from './app/config/auth.config';
import { provideRecaptchaConfig } from './app/config/recaptcha.config';
import { MsalInitService } from './app/services/msal-init.service';
import { MsalCleanupService } from './app/services/msal-cleanup.service';

// Factory function to initialize MSAL
export function initializeMsal(msalInitService: MsalInitService) {
  return () => msalInitService.getMsalInstance();
}

// Create cleanup service first
const cleanupService = new MsalCleanupService();

// Check for persisted interaction state and clean if necessary
// REMOVED: MSAL needs this state when returning from a login redirect!
// Do not clear the interaction state manually here.

// Create MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and then bootstrap the application
msalInstance.initialize().then(async () => {
  console.log('✅ MSAL instance initialized successfully');
  
  // Handle any pending redirect promise
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    if ((error as any)?.message?.includes('interaction_in_progress')) {
      console.warn('⚠️ Error de interacción en redirect promise, limpiando...');
      cleanupService.clearInteractionState();
    }
  }
  
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        BrowserModule,
        MsalModule.forRoot(msalInstance, msalGuardConfig, msalInterceptorConfig)
      ),
      provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })),
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimations(),
      ...provideRecaptchaConfig(),
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      MsalInitService,
      MsalCleanupService,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthHeaderInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: PageLoadingInterceptor,
        multi: true
      },
      {
        provide: RouteReuseStrategy,
        useClass: ChatReuseStrategy
      }
    ]
  }).catch(err => console.error('Bootstrap error:', err));
}).catch(err => {
  console.error('MSAL initialization failed:', err);
  // Show error message to user
  document.body.innerHTML = `
    <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial, sans-serif;">
      <div style="text-align: center; padding: 20px; border: 1px solid #dc3545; border-radius: 8px; background: #f8d7da; color: #721c24;">
        <h2>Error de Inicialización</h2>
        <p>No se pudo inicializar el sistema de autenticación.</p>
        <p>Por favor, recarga la página o contacta al administrador.</p>
        <button onclick="window.location.reload()" style="padding: 10px 20px; background: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer;">Recargar Página</button>
      </div>
    </div>
  `;
});

